import type { styleFn } from 'styled-system';

export type TransientStyleFn = (category: styleFn) => styleFn;

export type TransientProps<T> = {
  [K in keyof T as K extends string ? `$${K}` : never]?: T[K];
};

export type WithTransientProps<T> = T & TransientProps<T>;

/**
 * Creates a transient-enabled wrapper around a style/category function.
 *
 * Transient props (keys prefixed with `$`) are used for styling but should not
 * be forwarded to the DOM. This wrapper strips the `$` prefix and passes the
 * cleaned props to the underlying style function, preventing React warnings
 * about unknown DOM attributes.
 *
 * This utility allows components to safely accept both regular props and
 * transient style props without leaking them to DOM elements.
 *
 * @example
 * // Instead of forwarding raw style props to the DOM:
 * // <Box color="red" />      // ✅ Used for styling, not forwarded
 *
 * // Use transient props:
 * // <Box $color="red" />     // ✅ Used for styling, not forwarded
 *
 * @param category - A style/category function that receives props and returns
 *                   style output (e.g., CSS objects).
 *
 * @returns A wrapped function that removes `$` prefixes and passes the cleaned
 *          props to `category`. If no transient props are present, the original
 *          props are forwarded unchanged for performance.
 */
export const transient: TransientStyleFn = (category) => (props) => {
  const hasTransientProps = Object.keys(props).some((k) => k.startsWith('$'));

  if (!hasTransientProps) {
    return category(props);
  }

  const unprefixedProps = Object.keys(props).reduce<Record<string, unknown>>(
    (acc, key) => {
      const unprefixedKey = key.startsWith('$') ? key.substring(1) : key;
      acc[unprefixedKey] = props[key];
      return acc;
    },
    {},
  );

  return category(unprefixedProps);
};
